import { useLayoutEffect, useRef, useState } from 'react';
import { drawDOM, exportImage } from '@progress/kendo-drawing';

import Conditional from '../../common/conditional';

import { getCurrentUser, refreshUserSession } from '../../../services/authService';
import { configureSignature } from '../../../services/userService';

import { User } from '../../../utils/models';
import { summarize } from '../../../utils/utilities';

import { base64Upload } from '../../../services/fileUpload';
import logger from '../../../services/logService';
import classNames from './style.module.scss';

const Signature = () => {
	const [isModalVisible, setModalVisible] = useState(false);

	const signatureRef = useRef<HTMLDivElement>(null);
	const signatureImageRef = useRef<HTMLDivElement>(null);

	const { role, signature, firstName, lastName, id } = getCurrentUser() as User;
	const { manageReports, approveReports, reviewReports, viewReports } = role;

	const renderSignatureCopy = () => {
		let copySuffix = '';

		if (manageReports) copySuffix += 'create';
		copySuffix += viewReports ? 'accept' : approveReports ? 'review or approve' : '';

		return <p>Your signature will be appended to reports you {copySuffix}.</p>;
	};

	const generateSignature = async () => {
		if (!signatureImageRef.current) return;

		try {
			const group = await drawDOM(signatureImageRef.current);
			const base64ImageString = await exportImage(group);

			const imageUrl = await base64Upload(firstName + lastName, base64ImageString);
			if (imageUrl) {
				await configureSignature(imageUrl);
				await refreshUserSession();
			}
		} catch (error) {
			logger.log(error as Error);
		}
	};

	useLayoutEffect(() => {
		if (!signature) {
			const signatureView = signatureRef.current;
			if (signatureView) signatureView.scrollIntoView({ behavior: 'smooth' });

			generateSignature();
		}

		setModalVisible(!signature);
	}, [signature]);

	return (
		<Conditional isVisible={manageReports || viewReports || reviewReports || approveReports}>
			<div className={classNames.signature} ref={signatureRef}>
				<Conditional isVisible={isModalVisible}>
					<button
						className={classNames.signatureModalClose}
						onClick={() => setModalVisible(false)}
					>
						<i className="bi bi-x-lg"></i>
					</button>
				</Conditional>

				<div className={classNames.signatureIcon}>
					<i className="bi bi-file-richtext-fill"></i>
				</div>

				<h2>Signature</h2>

				{renderSignatureCopy()}

				<div className={classNames.signatureGenerator} ref={signatureImageRef}>
					<div className={classNames.signatureGeneratorUserName}>
						{firstName} {lastName}
					</div>
					<div className={classNames.signatureGeneratorID}>
						[{summarize(id.toString(), 15)}]
					</div>
				</div>
			</div>

			<Conditional isVisible={isModalVisible}>
				<div className={classNames.signatureModal}></div>
			</Conditional>
		</Conditional>
	);
};

export default Signature;
